/**
 * Retour automatique vers une page publique après connexion (spec cartes § 9.3).
 *
 * Cas couverts : connexion OAuth (qui revient toujours sur `/`) et nouveau
 * compte envoyé à l'onboarding. La page d'origine pose sa cible AVANT de
 * quitter ; `indexRoute` et la fin de l'onboarding la consomment.
 *
 * Sécurité : seuls des chemins INTERNES d'une liste blanche sont acceptés
 * (jamais une URL absolue) → aucun open redirect possible, même si le
 * localStorage est manipulé. Durée de validité bornée : un retour oublié ne
 * détourne pas une connexion ultérieure.
 */

const KEY = 'jr_return_to';
export const RETURN_TO_TTL_MS = 30 * 60_000;

const ALLOWED = [
  /^\/card\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
  // Page d'achat d'une SKILLCARD (migration 0304).
  /^\/card\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/acheter$/i,
  /^\/pack\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/acheter$/i,
  // Achat express d'un pack / d'un programme depuis le site.
  /^\/debloquer\/(pack|programme)\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
  // QR code d'un profil (migration 0334).
  /^\/connexion\/[A-HJKMNP-Z2-9]{8}$/,
  // Défi live privé (migration 0650).
  /^\/defi-live\/[A-HJKMNP-Z2-9]{8}$/,
  // Trousse offerte (migration 0738).
  /^\/trousse\/[0-9a-f]{16}$/,
];

/**
 * Plusieurs portes consomment le retour au MÊME moment (fin d'onboarding : la
 * redirection du routeur ET l'effet de la page). Sans ce verrou, la première
 * obtient la carte et la seconde, ne trouvant plus rien, renvoie vers le
 * tableau de bord — qui gagnait selon l'ordre (appareil lent). Pendant
 * quelques secondes, un second appel reçoit donc la même cible.
 */
export const RETURN_TO_LATCH_MS = 10_000;
let latch: { path: string; at: number } | null = null;

/** Tests seulement : oublie la dernière cible consommée. */
export function resetReturnToLatch(): void {
  latch = null;
}

export function isAllowedReturnPath(path: unknown): path is string {
  return typeof path === 'string' && ALLOWED.some((re) => re.test(path));
}

export function setReturnTo(path: string, now = Date.now()): void {
  if (!isAllowedReturnPath(path)) return;
  latch = null;
  try {
    window.localStorage.setItem(KEY, JSON.stringify({ path, at: now }));
  } catch {
    /* stockage indisponible (navigation privée) : pas de retour auto, sans casser */
  }
}

/** Une cible valide attend (sans la consommer) — ex. sauter une étape d'onboarding. */
export function hasPendingReturnTo(now = Date.now()): boolean {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { path?: unknown; at?: unknown };
    return (
      typeof parsed.at === 'number' &&
      now - parsed.at <= RETURN_TO_TTL_MS &&
      isAllowedReturnPath(parsed.path)
    );
  } catch {
    return false;
  }
}

/**
 * Lit ET efface la cible. `null` si absente, expirée ou invalide. Un second
 * appel dans les `RETURN_TO_LATCH_MS` rend la même cible (cf. `latch`).
 */
export function consumeReturnTo(now = Date.now()): string | null {
  const stored = readAndClear(now);
  if (stored) {
    latch = { path: stored, at: now };
    return stored;
  }
  if (latch && now - latch.at <= RETURN_TO_LATCH_MS) return latch.path;
  latch = null;
  return null;
}

function readAndClear(now: number): string | null {
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(KEY);
    window.localStorage.removeItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { path?: unknown; at?: unknown };
    if (typeof parsed.at !== 'number' || now - parsed.at > RETURN_TO_TTL_MS) return null;
    return isAllowedReturnPath(parsed.path) ? parsed.path : null;
  } catch {
    return null;
  }
}
